/**
 * useUserRuns Hook
 * 
 * Fetches and manages all pipeline runs for the current user.
 * Automatically connects to SSE streams for active runs.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import progressService from '../services/progressService';
import { useProgressStream } from './useProgressStream';
import { transformTaskForList, transformProgressData } from '../utils/progressDataTransformer';

export const useUserRuns = (userEmail, options = {}) => {
  const {
    autoRefresh = true, // Automatically refresh task list
    refreshInterval = 30000, // Refresh every 30 seconds
    connectToActiveStreams = false, // Disable multi-run SSE by default (prevents backend thread pressure)
    projectType = null
  } = options;

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runProgress, setRunProgress] = useState({}); // { runId: progressData }
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isRefreshingRef = useRef(false); // Prevent concurrent refresh requests
  const runsRef = useRef([]); // Keep ref of runs to avoid dependency issues
  const streamConnectionsRef = useRef(new Map()); // run_id -> EventSource

  const mergeProgressIntoRun = useCallback((run, progress) => {
    if (!run || !progress || typeof progress !== 'object') return run;

    const services = progress.services || {};
    const serviceNames = Object.keys(services);
    const nextOverall = Number(progress.overall_progress);
    const completedServices = Number(progress.completed_services || 0);
    const totalServices = Number(progress.total_services || 0);

    let nextStatus = run.status || 'pending';
    if (totalServices > 0 && completedServices >= totalServices) {
      nextStatus = 'completed';
    } else if ((Number.isFinite(nextOverall) && nextOverall > 0) || completedServices > 0) {
      nextStatus = 'processing';
    }

    return {
      ...run,
      overall_progress: Number.isFinite(nextOverall) ? Math.round(nextOverall) : (run.overall_progress ?? 0),
      status: nextStatus,
      services: run.services && run.services.length > 0 ? run.services : serviceNames,
      services_progress: serviceNames.length > 0 ? services : (run.services_progress || {}),
      last_updated: new Date()
    };
  }, []);

  // Load user runs
  const loadRuns = useCallback(async (silent = false) => {
    if (!userEmail) {
      console.warn('⚠️ useUserRuns: No user email provided');
      setLoading(false);
      setError('User email not available. Please log in again.');
      return;
    }

    // Prevent concurrent refresh requests
    if (isRefreshingRef.current) {
      console.log('⏸️ Refresh already in progress, skipping...');
      return;
    }

    isRefreshingRef.current = true;

    try {
      // Only show loading spinner on initial load, not on background refreshes
      if (!silent && isInitialLoad) {
        setLoading(true);
      }
      // Don't clear error on silent refreshes (keep showing it if it exists)
      if (!silent) {
        setError(null);
      }
      
      console.log(`📊 Loading runs for user: ${userEmail}`);
      const tasks = await progressService.getUserTasks(userEmail, projectType);
      console.log(`✅ Loaded ${tasks.length} tasks for user`);
      
      // Transform tasks to list format
      const transformedRuns = tasks.map(task => {
        const transformed = transformTaskForList(task);
        return transformed;
      });
      
      // Sort by last_updated (newest first)
      transformedRuns.sort((a, b) => {
        const timeA = a.last_updated?.getTime() || 0;
        const timeB = b.last_updated?.getTime() || 0;
        return timeB - timeA;
      });
      
      setRuns(transformedRuns);
      runsRef.current = transformedRuns; // Update ref

      // OPTIMIZATION: Only load progress for active runs to reduce API calls
      // Even on initial load, we don't need progress for completed/pending runs
      // The task list already has overall_progress from the getUserTasks response
      const activeRuns = transformedRuns.filter(run => 
        run.status === 'processing' || run.status === 'running'
      );
      
      // Only load detailed progress for active runs (max 1 to prevent overload)
      // Completed/pending runs already have overall_progress from the task list
      const maxConcurrentProgress = 1;
      const runsToLoad = activeRuns.slice(0, maxConcurrentProgress);
      
      // Only load progress if we don't already have it cached
      const runsNeedingProgress = runsToLoad.filter(run => {
        const existingProgress = runProgress[run.run_id];
        // Only load if we don't have progress or if it's stale (older than 30 seconds)
        return !existingProgress;
      });
      
      if (runsNeedingProgress.length > 0) {
        // Load progress with a small delay between requests to prevent overwhelming server
        const progressPromises = runsNeedingProgress.map(async (run, index) => {
          // Stagger requests slightly to prevent thundering herd
          if (index > 0) {
            await new Promise(resolve => setTimeout(resolve, 100 * index));
          }
          try {
            const progress = await progressService.getAggregateProgress(run.run_id);
            return { runId: run.run_id, progress };
          } catch (err) {
            console.error(`Error loading progress for run ${run.run_id}:`, err);
            return null;
          }
        });

        const progressResults = await Promise.all(progressPromises);
        setRunProgress(prev => {
          const progressMap = { ...prev };
          progressResults.forEach(result => {
            if (result) {
              progressMap[result.runId] = result.progress;
            }
          });
          return progressMap;
        });
      }
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }

    } catch (err) {
      console.error('❌ Error loading user runs:', err);
      const errorMessage = err.response?.status === 404 
        ? 'Progress tracking endpoint not found. Please ensure the backend service is running and progress routes are registered.'
        : err.response?.status === 401
        ? 'Authentication required. Please log in again.'
        : err.message || 'Failed to load pipeline runs';
      
      // Only show error on initial load or manual refresh, not on silent background refreshes
      if (!silent) {
        setError(errorMessage);
        setRuns([]); // Set empty array on error
      } else {
        // On silent refresh errors, just log them but don't disrupt the UI
        console.warn('⚠️ Silent refresh failed (will retry on next interval):', errorMessage);
      }
    } finally {
      // Mark initial load as complete after first load attempt
      if (isInitialLoad) {
        setIsInitialLoad(false);
        // Always set loading to false after initial load completes
        setLoading(false);
      }
      // For silent refreshes, don't touch the loading state at all
      isRefreshingRef.current = false;
    }
  }, [userEmail, isInitialLoad, projectType]);

  // Initial load
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Auto-refresh - use a reasonable interval to prevent server overload
  useEffect(() => {
    if (!autoRefresh || !userEmail) return;

    // Check if there are active runs - if not, refresh less frequently
    const checkAndRefresh = () => {
      const currentRuns = runsRef.current;
      const hasActiveRuns = currentRuns.some(run => 
        run.status === 'processing' || run.status === 'running'
      );
      
      // Only refresh if there are active runs, or use longer interval if no active runs
      if (hasActiveRuns) {
        loadRuns(true);
      } else {
        // No active runs - refresh less frequently (every 2 minutes)
        setTimeout(() => {
          loadRuns(true);
        }, 120000); // 2 minutes
      }
    };

    // Initial check
    const timeoutId = setTimeout(checkAndRefresh, Math.max(refreshInterval, 30000));
    
    // Set up interval for active runs
    const intervalId = setInterval(() => {
      const currentRuns = runsRef.current;
      const hasActiveRuns = currentRuns.some(run => 
        run.status === 'processing' || run.status === 'running'
      );
      
      // Only refresh if there are active runs
      if (hasActiveRuns) {
        loadRuns(true);
      }
    }, Math.max(refreshInterval, 30000)); // Minimum 30 seconds

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, userEmail, loadRuns]);

  // Real-time streams for active runs (SSE).
  useEffect(() => {
    if (!connectToActiveStreams || !userEmail) {
      return;
    }

    // Keep at most one SSE stream open: the newest active run only.
    const activeRunIds = new Set(
      (runsRef.current || [])
        .filter((run) =>
          run?.run_id &&
          (run.status === 'processing' || run.status === 'running') &&
          Number(run.overall_progress || 0) < 100
        )
        .sort((a, b) => {
          const ta = a.last_updated?.getTime() || a.created_at?.getTime() || 0;
          const tb = b.last_updated?.getTime() || b.created_at?.getTime() || 0;
          return tb - ta;
        })
        .slice(0, 1)
        .map((run) => run.run_id)
    );

    // Close stale streams.
    streamConnectionsRef.current.forEach((source, runId) => {
      if (!activeRunIds.has(runId)) {
        try {
          source.close();
        } catch (_) {
          // ignore
        }
        streamConnectionsRef.current.delete(runId);
      }
    });

    // Open streams for active runs not yet connected.
    for (const run of runsRef.current || []) {
      const runId = run?.run_id;
      if (!runId || !activeRunIds.has(runId) || streamConnectionsRef.current.has(runId)) {
        continue;
      }

      const source = progressService.connectToProgressStream(
        runId,
        (progress) => {
          setRunProgress((prev) => ({ ...prev, [runId]: progress }));
          setRuns((prevRuns) => {
            const nextRuns = prevRuns.map((r) => (r.run_id === runId ? mergeProgressIntoRun(r, progress) : r));
            nextRuns.sort((a, b) => {
              const timeA = a.last_updated?.getTime() || a.created_at?.getTime() || 0;
              const timeB = b.last_updated?.getTime() || b.created_at?.getTime() || 0;
              return timeB - timeA;
            });
            runsRef.current = nextRuns;
            return nextRuns;
          });
        },
        () => {
          // Keep auto-reconnect behavior inside EventSource; if closed later, polling still refreshes.
        },
        (completedProgress) => {
          setRunProgress((prev) => ({ ...prev, [runId]: completedProgress }));
          setRuns((prevRuns) => {
            const nextRuns = prevRuns.map((r) => (r.run_id === runId ? mergeProgressIntoRun(r, completedProgress) : r));
            runsRef.current = nextRuns;
            return nextRuns;
          });

          const existing = streamConnectionsRef.current.get(runId);
          if (existing) {
            try {
              existing.close();
            } catch (_) {
              // ignore
            }
            streamConnectionsRef.current.delete(runId);
          }
        }
      );

      streamConnectionsRef.current.set(runId, source);
    }

    return () => {
      // Keep active streams across re-renders; full cleanup on unmount below.
    };
  }, [runs, connectToActiveStreams, userEmail, mergeProgressIntoRun]);

  // Cleanup all streams on unmount.
  useEffect(() => {
    return () => {
      streamConnectionsRef.current.forEach((source) => {
        try {
          source.close();
        } catch (_) {
          // ignore
        }
      });
      streamConnectionsRef.current.clear();
    };
  }, []);

  // Get transformed progress data for a specific run
  const getRunProgressData = useCallback((runId) => {
    const progress = runProgress[runId];
    const run = runs.find(r => r.run_id === runId);
    
    if (!progress && !run) return null;
    
    return transformProgressData(progress || {}, {
      run_id: run?.run_id,
      project_id: run?.project_id,
      project_name: run?.name,
      created_at: run?.created_at,
      user_email: run?.user_email,
      input_files: []
    });
  }, [runs, runProgress]);

  // Update progress for a specific run
  const updateRunProgress = useCallback((runId, progressData) => {
    setRunProgress(prev => ({
      ...prev,
      [runId]: progressData
    }));
    setRuns((prevRuns) => {
      const nextRuns = prevRuns.map((run) => (run.run_id === runId ? mergeProgressIntoRun(run, progressData) : run));
      runsRef.current = nextRuns;
      return nextRuns;
    });
  }, [mergeProgressIntoRun]);

  const removeRun = useCallback(async (runId) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) {
      throw new Error('Run ID is required');
    }
    if (!userEmail) {
      throw new Error('User email is required');
    }

    // Optimistic UI update: remove immediately from list/progress map.
    setRuns((prevRuns) => {
      const nextRuns = prevRuns.filter((run) => {
        const runKey = String(run?.run_id || '');
        const fullRunId = String(run?.full_run_id || '');
        return runKey !== normalizedRunId && fullRunId !== normalizedRunId;
      });
      runsRef.current = nextRuns;
      return nextRuns;
    });
    setRunProgress((prev) => {
      const next = { ...prev };
      delete next[normalizedRunId];
      return next;
    });

    const existingStream = streamConnectionsRef.current.get(normalizedRunId);
    if (existingStream) {
      try {
        existingStream.close();
      } catch (_) {
        // ignore
      }
      streamConnectionsRef.current.delete(normalizedRunId);
    }

    try {
      return await progressService.deleteRun(userEmail, normalizedRunId);
    } catch (err) {
      console.error(`❌ Error deleting run ${normalizedRunId}:`, err);
      setError(err?.message || 'Failed to delete run');
      await loadRuns(true);
      throw err;
    }
  }, [userEmail, loadRuns]);

  return {
    runs,
    loading,
    error,
    refresh: loadRuns,
    removeRun,
    getRunProgressData,
    updateRunProgress,
    runProgress
  };
};

/**
 * Hook to manage SSE connection for a single run
 */
export const useRunProgressStream = (runId, enabled = true) => {
  const { progress, error, isConnected } = useProgressStream(runId, {
    enabled: enabled && !!runId,
    onComplete: (completedProgress) => {
      console.log(`Run ${runId} completed:`, completedProgress);
    }
  });

  return {
    progress,
    error,
    isConnected,
    progressData: progress ? transformProgressData(progress, { run_id: runId }) : null
  };
};

